import { Box, Button, Paper, TextField, Typography } from '@mui/material';

import { Invoice } from 'openapi';

import { useTranslations } from 'context/TranslationContext';

import {
  validateRegistrationNumber,
  validateVatNumber
} from 'utils/helpers/invoiceHelpers';

import { cardContentStyle, cardHeaderStyle } from 'styles/shared/CardStyle';

import { useInlineCounterpartyCreate } from './hooks/useInlineCounterpartyCreate';

interface NewCounterpartyCardProps {
  currentInvoice: Invoice;
  initialName: string;
  onSuccess: (name: string) => void;
}

export const NewCounterpartyCard = ({
  currentInvoice,
  initialName,
  onSuccess
}: NewCounterpartyCardProps) => {
  const { translate } = useTranslations();
  const {
    cardName,
    setCardName,
    cardVat,
    setCardVat,
    cardReg,
    setCardReg,
    isCreating,
    handleCreate
  } = useInlineCounterpartyCreate(currentInvoice, initialName, onSuccess);

  const isCardRegValid = validateRegistrationNumber(cardVat, cardReg);
  const isCardVatValid = validateVatNumber(cardVat, cardReg);

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #e5e7eb',
        borderRadius: '1rem',
        mt: 2,
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          ...cardHeaderStyle,
          justifyContent: 'space-between'
        }}
      >
        <Typography
          sx={{
            fontSize: '0.875rem',
            fontWeight: 600
          }}
        >
          {translate('labels.newCounterparty')}
        </Typography>
      </Box>
      <Box
        sx={{
          ...cardContentStyle,
          gap: 2
        }}
      >
        <TextField
          label={`${translate('labels.name')}*`}
          value={cardName}
          onChange={(event) => setCardName(event.target.value)}
          inputProps={{ 'data-testid': 'inline-create-name' }}
          fullWidth
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label={translate('labels.companyRegistrationNumber')}
            value={cardReg}
            onChange={(event) => setCardReg(event.target.value)}
            inputProps={{ 'data-testid': 'inline-create-reg' }}
            sx={{ flex: 1 }}
            error={!isCardRegValid && !!cardReg}
            helperText={
              !isCardRegValid && !!cardReg
                ? translate(
                    cardVat && !cardVat.startsWith('BG')
                      ? 'errors.counterpartyRegistrationNumberForeign'
                      : 'errors.counterpartyRegistrationNumberBG'
                  )
                : ''
            }
          />
          <TextField
            label={translate('labels.vatNumber')}
            value={cardVat}
            onChange={(event) => setCardVat(event.target.value)}
            inputProps={{ 'data-testid': 'inline-create-vat' }}
            sx={{ flex: 1 }}
            error={!isCardVatValid && !!cardVat}
            helperText={
              !isCardVatValid && !!cardVat
                ? translate('errors.counterpartyVatNumberEquality')
                : ''
            }
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            disabled={
              !cardName.trim() ||
              !isCardRegValid ||
              !isCardVatValid ||
              isCreating
            }
            onClick={handleCreate}
          >
            {translate('buttons.createCounterparty')}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};
